import React, { useEffect } from 'react';
import { 
  Modal, 
  Box, 
  Typography, 
  Grid, 
  Divider, 
  Card, 
  CardContent, 
  IconButton,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button
} from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import {translate} from "../../i18n/customI18nProvider";
import { formatCurrency } from '../../util';
import JsonDisplay from '../../components/JsonDisplay';
import { getBarDetails } from '../../services/bar.service';
import { getUserDetails } from '../../services/user.service';

const TransactionModal = ({ open, handleClose, transactionData, currency = 'CHF' }) => {

    const [displayMode, setDisplayMode] = React.useState('pretty');
    const [barName, setBarName] = React.useState('');
    const [userName, setUserName] = React.useState('');

    useEffect(() => {
        fetchBarDetails(transactionData?.bar_id);
        fetchUserDetails(transactionData?.user_id);
    }, [transactionData]);

    async function fetchBarDetails(bar_id) {
        if (!bar_id) return;
        const response = await getBarDetails(bar_id);
        if (response && !response.error) {
            setBarName(response.bar.name || '');
        } else {
            setBarName('');
        }
    }

    async function fetchUserDetails(user_id) {
        if (!user_id) return;
        const response = await getUserDetails(user_id);
        if (response && !response.error) {
            setUserName(response.email || '');
        } else {
            setUserName('');
        }
    }

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '80%', md: 1400 },
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: { xs: 2, md: 4 },
        overflowY: 'auto',
        borderRadius: 2,
    };

    function totalQuantity(order) {
        if (!order || order.length === 0) return 0;
        return order.reduce((total, item) => total + (item.quantity || 0), 0);
    }

    function toogleDisplayMode() {
        setDisplayMode(prevMode => prevMode === 'pretty' ? 'json' : 'pretty');
    }

    function getTransactionId() {
        if(!transactionData) return null;
        if(transactionData.referenceNumber) return transactionData.referenceNumber;
        if(transactionData.twint_direct_merchant_tran_id) return transactionData.twint_direct_merchant_tran_id;
        if(transactionData.card_transaction_id) return transactionData.card_transaction_id;
        if(transactionData.transaction_uuid) return transactionData.transaction_uuid;
        return null;
    }

    function prettyDisplay() {
        let refundableAmount = null;
        if(transactionData?.amount && transactionData?.refund_amount) {
            refundableAmount = transactionData.amount - transactionData.refund_amount;
        }
        return (
            <>
                <div style={{ marginTop: "15px" }}>
                    <Typography variant="h6" color="primary"> 
                    {translate('custom.general_information')}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.type')}</Typography>
                            <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{translate('custom.'+transactionData?.type)}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.amount')}</Typography>
                            <Typography variant="body1">{formatCurrency(transactionData?.amount)} {currency}</Typography>
                        </Grid>
                        {
                            refundableAmount !== null && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.amount_refundable')}</Typography>
                                    <Typography variant="body1">{formatCurrency(refundableAmount)} {currency}</Typography>
                                </Grid>
                            )
                        }
                        {
                            transactionData?.sepa_tax && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.sepa_tax')}</Typography>
                                    <Typography variant="body1">{formatCurrency(transactionData?.sepa_tax)} {currency}</Typography>
                                </Grid>
                            )
                        }
                        {
                            transactionData?.sepa_exchange_rate && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.sepa_exchange_rate')}</Typography>
                                    <Typography variant="body1">1.00 CHF = {formatCurrency(transactionData?.sepa_exchange_rate)} €</Typography>
                                </Grid>
                            )
                        }
                        {
                            transactionData?.sepa_tax && transactionData?.sepa_exchange_rate && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.sepa_real_amount')}</Typography>
                                    <Typography variant="body1">{(transactionData.amount - transactionData.sepa_tax) * transactionData.sepa_exchange_rate} €</Typography>
                                </Grid>
                            )
                        }
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.date')}</Typography>
                            <Typography variant="body1">{transactionData?.date}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.transaction_id')}</Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                {getTransactionId() ?? '-'}
                            </Typography>
                        </Grid>
                        {
                            transactionData?.pan && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.card_number')}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {transactionData?.pan.replace('*', '#') ?? '-'}
                                    </Typography>
                                </Grid>
                            )
                        }
                        {
                            transactionData?.type == 'checkout' && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.forced')}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {transactionData?.forced ? translate('custom.yes') : translate('custom.no')}
                                    </Typography>
                                </Grid>
                            )
                        }
                        {
                            transactionData?.type == 'debit' && (
                                <>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="textSecondary">{translate('custom.table_number')}</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {transactionData?.table_number ?? '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="textSecondary">{translate('custom.order_number')}</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {transactionData?.order_number ?? '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="textSecondary">{translate('custom.consumption')}</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {transactionData?.amount_split?.consumption ? `${formatCurrency(transactionData?.amount_split?.consumption)} ${currency}` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="textSecondary">{translate('custom.deposit')}</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {transactionData?.amount_split?.deposit ? `${formatCurrency(transactionData?.amount_split?.deposit)} ${currency}` : '-'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Typography variant="caption" color="textSecondary">{translate('custom.deposit_return')}</Typography>
                                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                            {transactionData?.amount_split?.deposit_return ? `${formatCurrency(transactionData?.amount_split?.deposit_return)} ${currency}` : '-'}
                                        </Typography>
                                    </Grid>
                                </>
                            )                            
                        }
                        {
                            transactionData?.type == 'credit' && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="textSecondary">{translate('custom.payment_type')}</Typography>
                                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                        {transactionData?.payment_type ? translate(`custom.${transactionData?.payment_type}`) : '-'}
                                    </Typography>
                                </Grid>
                            )
                        }
                    </Grid>
                </div>


                {transactionData?.smartphone_number && transactionData?.gps_lat && transactionData?.gps_long && (
                    <div style={{ marginTop: "15px" }}>
                        <Typography variant="h6" color="primary">
                            {translate('custom.device_information')}
                        </Typography>
                        <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.device')}</Typography>
                            <Typography variant="body1">
                                {transactionData?.smartphone_number} ({translate(`custom.${transactionData?.phone_type}`)})
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.coordinates')}</Typography>
                            <Typography variant="body1">
                                {transactionData?.gps_lat}, {transactionData?.gps_long}
                                <a
                                    href={`https://www.google.com/maps?q=${transactionData?.gps_lat},${transactionData?.gps_long}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginLeft: '10px', textDecoration: 'none', color: '#1976d2' }}
                                >
                                    Voir sur Google Maps
                                </a>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.altitude')}</Typography>
                            <Typography variant="body1">{transactionData?.gps_alt ? Math.round(transactionData?.gps_alt) : '-'} m</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.gps_accuracy')}</Typography>
                            <Typography variant="body1">{transactionData?.gps_accuracy ? Math.round(transactionData?.gps_accuracy) : '-'} m</Typography>
                        </Grid>
                        </Grid>
                    </div>
                )}

                {transactionData?.order && transactionData?.order.length > 0 && (
                    <div style={{ marginTop: "15px" }}>
                        <Typography variant="h6" color="primary">
                            {translate('custom.order_details')} ({transactionData?.order.length} {translate('custom.item')}{transactionData?.order.length > 1 ? 's' : ''})
                        </Typography>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{translate('custom.name')}</TableCell>
                                    <TableCell>{translate('custom.minimum_age')}</TableCell>
                                    <TableCell>{translate('custom.validity')}</TableCell>
                                    <TableCell align="right">{translate('custom.quantity')}</TableCell>
                                    <TableCell align="right">{translate('custom.unit_price')}</TableCell>
                                    <TableCell align="right">{translate('custom.total')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactionData?.order.map((item, index) => (
                                    <TableRow key={item._id || index}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.min_age == 0 ? '-' : `${item.min_age} ans`}</TableCell>
                                        <TableCell>
                                            {translate('custom.from')} {new Date(item.start_date).toLocaleDateString()} {translate('custom.to')} {new Date(item.end_date).toLocaleDateString()}
                                        </TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.price)} {currency}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.total)} {currency}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell colSpan={3} align="left"><strong>{translate('custom.total')}</strong></TableCell>
                                    <TableCell align="right"><strong>{totalQuantity(transactionData?.order)}</strong></TableCell>
                                    <TableCell align="right">-</TableCell>
                                    <TableCell align="right"><strong>{formatCurrency(transactionData?.amount)} {currency}</strong></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </div>
                )}

                <div style={{ marginTop: "15px" }}>
                    <Typography variant="h6" color="primary">
                        {translate('custom.identifiers')}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.client_uid')}</Typography>
                            <Typography variant="body2">{transactionData?.client_uid}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.object_id')}</Typography>
                            <Typography variant="body2">{transactionData?._id}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.user')}</Typography>
                            <Typography variant="body2">{userName} ({transactionData?.user_id})</Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">{translate('custom.point_of_sale')}</Typography>
                            <Typography variant="body2">{barName} ({transactionData?.bar_id})</Typography>
                        </Grid>
                    </Grid>
                </div>
            </>
        )
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="transaction-modal-title"
        >
            <Box sx={modalStyle}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Typography id="transaction-modal-title" variant="h5" component="h2" fontWeight="bold">
                        {translate('custom.transaction_details')}
                    </Typography>
                    <Button variant="outlined" onClick={toogleDisplayMode} sx={{ mr: 2 }}>
                        {displayMode === 'pretty' ? translate('custom.view_json') : translate('custom.view_pretty')}
                    </Button>
                </div>
                <IconButton onClick={handleClose} size="small">
                    <CloseIcon />
                </IconButton>
                </Box>
                <Divider sx={{ mb: 3 }} />
        
                {displayMode === 'pretty' ? prettyDisplay() : <JsonDisplay root={transactionData} />}
                
            </Box>
        </Modal>
    );
};

export default TransactionModal;